import React from 'react';
import { CitationBadge } from './CitationBadge';
import type { ConversationBubbleProps } from '../../types/emma.types';
import ReactMarkdown from 'react-markdown';
import { sanitizeChatContent, extractArtifactPayload } from '@/utils/sanitizeChatContent';
import UniversalPreview from '@/components/workflow/steps/UniversalPreview';
import { apiFetch } from '@/lib/api';
import { buildApiUrl } from '@/lib/apiBase';
import { workflowService } from '@/services/api/workflow/workflowService';

/**
 * ConversationBubble Component - Individual message display
 * Supports different message types including strategic recommendations
 * Integrates with agent-first UI design patterns
 * EMMA-HARDEN-002: Now includes inline citation badges
 */
export function ConversationBubble({
  message,
  isOwn,
  showTimestamp = true,
  onRecommendationClick,
  className = ''
}: ConversationBubbleProps) {
  const [isThinkingExpanded, setIsThinkingExpanded] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState<'idle' | 'success' | 'error'>('idle');
  const [savedCampaignName, setSavedCampaignName] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');

  const handleSaveToCampaign = async () => {
    if (!message.metadata?.statelessAsset) return;
    setIsSaving(true);
    setSaveStatus('idle');
    setErrorMessage('');
    
    try {
      const asset = message.metadata.statelessAsset.asset;
      const citations = message.metadata.statelessAsset.citations || [];
      const asset_type = message.metadata.assetType || 'battlecard';
      const deal_context = message.metadata.dealContext || {};
      
      // 1. Fetch clients
      const clients = await apiFetch<any[]>('/api/v1/ecosystem/clients').catch(() => []);
      if (!clients || clients.length === 0) {
        throw new Error('No clients found in the ecosystem database. Please create a client profile first.');
      }
      
      // 2. Find matching client or default to the first client
      const domainLower = (deal_context.account_domain || '').toLowerCase();
      const match = clients.find((c: any) => c.name?.toLowerCase().includes(domainLower.split('.')[0]) || domainLower.includes(c.name?.toLowerCase()));
      const selectedClient = match || clients[0];
      
      // 3. Create campaign
      const campaignName = `Emma Quick Generate - ${asset_type.toUpperCase()} - ${deal_context.account_domain || 'Deal'}`;
      const campaignResponse = await apiFetch<any>('/api/v1/campaigns', {
        method: 'POST',
        body: JSON.stringify({
          client_id: selectedClient.id,
          client_name: selectedClient.name,
          name: campaignName
        })
      });
      
      const campId = campaignResponse.campaign_id || campaignResponse.id;
      if (!campId) {
        throw new Error('Failed to retrieve campaign ID from creation response.');
      }
      
      // 4. Create workflow for this campaign
      const activeWorkflow = await workflowService.createWorkflow({
        campaign_id: campId,
        client_name: selectedClient.name
      });
      
      // 5. Build content piece structure for Step 9
      const asset_body = asset?.deep_dive || asset?.all || Object.values(asset || {})[0] || '';
      
      const contentPiece = {
        content_id: `content_${Date.now()}`,
        channel: asset_type,
        content_type: asset_type,
        title: `${asset_type.toUpperCase()} - ${deal_context.account_domain || 'Deal'}`,
        body: asset_body,
        cta: 'Learn More',
        citations: citations,
        claim_mappings: [],
        matched_recipe: {},
        layout_type: 'genui_html',
        dynamic_layout: true
      };
      
      // 6. Save under Step 9 step data
      await workflowService.updateStep(activeWorkflow.id, 9, {
        status: 'completed',
        data: {
          content_pieces: [contentPiece]
        }
      });
      
      setSavedCampaignName(campaignName);
      setSaveStatus('success');
    } catch (err: any) {
      console.error('Save to campaign failed:', err);
      setErrorMessage(err instanceof Error ? err.message : 'Unknown error during save operation.');
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  React.useEffect(() => {
    if (message.content) {
      setIsThinkingExpanded(false);
    }
  }, [message.content]);

  const renderWithCaret = (children: React.ReactNode) => {
    const replaceInText = (text: string) => {
      if (!text.includes('||cursor||')) return text;
      const parts = text.split('||cursor||');
      return (
        <>
          {parts[0]}
          <span className="inline-block animate-pulse text-emma-500 font-bold ml-0.5 align-middle">▊</span>
          {parts[1]}
        </>
      );
    };

    if (typeof children === 'string') {
      return replaceInText(children);
    }

    if (Array.isArray(children)) {
      return children.map((child, index) => {
        if (typeof child === 'string') {
          return <React.Fragment key={index}>{replaceInText(child)}</React.Fragment>;
        }
        return child;
      });
    }

    return children;
  };

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderRecommendations = () => {
    if (!message.metadata?.agentRecommendations) return null;

    return (
      <div className="mt-3 space-y-2">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Strategic Recommendations:
        </h4>
        {message.metadata.agentRecommendations.map((rec, index) => (
          <div
            key={index}
            className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
            onClick={() => onRecommendationClick?.(rec)}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                {rec.agentName}
              </span>
              <span className={`px-2 py-1 text-xs rounded-full ${
                rec.priority === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200' :
                rec.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200' :
                rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200' :
                'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200'
              }`}>
                {rec.priority}
              </span>
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-1">
              {rec.action}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              {rec.reasoning}
            </p>
          </div>
        ))}
      </div>
    );
  };

  const renderStrategicInsights = () => {
    if (!message.metadata?.strategicInsights) return null;

    return (
      <div className="mt-3 space-y-2">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Strategic Insights:
        </h4>
        {message.metadata.strategicInsights.map((insight, index) => (
          <div
            key={index}
            className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-3"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-purple-800 dark:text-purple-200 capitalize">
                {insight.category}
              </span>
              <div className="flex items-center space-x-2">
                {insight.actionable && (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 rounded-full">
                    Actionable
                  </span>
                )}
                <span className={`px-2 py-1 text-xs rounded-full ${
                  insight.impact === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200' :
                  insight.impact === 'medium' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200'
                }`}>
                  {insight.impact} impact
                </span>
              </div>
            </div>
            <p className="text-sm text-purple-700 dark:text-purple-300">
              {insight.insight}
            </p>
          </div>
        ))}
      </div>
    );
  };

  const renderWorkflowSuggestions = () => {
    if (!message.metadata?.workflowSuggestions) return null;

    return (
      <div className="mt-3 space-y-2">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Workflow Suggestions:
        </h4>
        {message.metadata.workflowSuggestions.map((workflow, index) => (
          <div
            key={index}
            className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-green-800 dark:text-green-200">
                {workflow.name}
              </span>
              <span className="text-xs text-green-600 dark:text-green-400">
                ~{Math.round(workflow.estimatedTime / 60)}min
              </span>
            </div>
            <p className="text-sm text-green-700 dark:text-green-300 mb-2">
              {workflow.description}
            </p>
            <div className="flex flex-wrap gap-1">
              {workflow.agents.map((agent, agentIndex) => (
                <span
                  key={agentIndex}
                  className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 rounded-full"
                >
                  {agent}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4 ${className}`}>
      <div className={`max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
        {/* Avatar */}
        {!isOwn && (
          <div className="flex items-center mb-1">
            <div className="w-8 h-8 flex items-center justify-center bg-emma-500 rounded-full text-white text-sm font-semibold mr-2 shadow-md">
              E
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Emma
            </span>
          </div>
        )}

        {/* Message bubble */}
        <div
          className={`px-4 py-3 rounded-2xl ${
            isOwn
              ? 'bg-primary-600 text-white rounded-br-md'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-md'
          } ${
            message.type === 'error' 
              ? 'bg-red-100 border border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-200'
              : ''
          }`}
        >
          {!isOwn && (message.thinkingContent || message.metadata?.thinkingContent) && (() => {
            const rawThinking = message.thinkingContent || message.metadata?.thinkingContent || '';
            const steps = rawThinking.split('\n').map(s => s.trim()).filter(Boolean);
            const isActive = !message.isComplete;
            const lastStep = steps[steps.length - 1] || '';
            const lastMatch = lastStep.match(/^([\uD800-\uDBFF][\uDC00-\uDFFF]|\p{Emoji_Presentation}|\p{Emoji}\uFE0F?)/u);
            const lastText = lastMatch ? lastStep.substring(lastMatch[0].length).trim() : lastStep.trim();
            const emoji = lastMatch ? lastMatch[0] : '⚡';
            if (!isActive) return null;
            return (
              <div className="emma-thinking-card">
                <div className="emma-thinking-wave">
                  <span className="wave-dot"></span>
                  <span className="wave-dot"></span>
                  <span className="wave-dot"></span>
                </div>
                <span className="emma-thinking-step-bullet">{emoji}</span>
                <span className="emma-thinking-active-text">{lastText}...</span>
                <div className="emma-thinking-shimmer" />
              </div>
            );
          })()}

          {/* Main message content */}
          <div className="text-sm leading-relaxed whitespace-pre-wrap markdown-bubble">
            {message.content ? (
              <ReactMarkdown
                components={{
                  a: ({ href, children, ...props }) => (
                    <a
                      {...props}
                      href={href}
                      onClick={(e) => {
                        e.preventDefault();
                        const linkText = String(children || '');
                        if (
                          linkText.includes('Open Template Preview') ||
                          linkText.includes('View Code') ||
                          (href && href.includes('/api/templates/preview/'))
                        ) {
                          const artifactPayload = extractArtifactPayload(message);
                          window.dispatchEvent(new CustomEvent('emma-open-artifact-panel', {
                            detail: artifactPayload
                          }));
                        } else if (href) {
                          const target = href.startsWith('/api/') ? buildApiUrl(href) : href;
                          window.open(target, '_blank');
                        }
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`underline font-semibold hover:opacity-80 transition-opacity ${
                        isOwn ? 'text-white' : 'text-blue-600 dark:text-blue-400'
                      }`}
                    >
                      {renderWithCaret(children)}
                    </a>
                  ),
                  ul: ({ ...props }) => (
                    <ul className="list-disc pl-4 my-2 space-y-1" {...props} />
                  ),
                  ol: ({ ...props }) => (
                    <ol className="list-decimal pl-4 my-2 space-y-1" {...props} />
                  ),
                  li: ({ children, ...props }) => (
                    <li className="text-sm" {...props}>
                      {renderWithCaret(children)}
                    </li>
                  ),
                  p: ({ children, ...props }) => (
                    <p className="mb-2 last:mb-0" {...props}>
                      {renderWithCaret(children)}
                    </p>
                  ),
                  h1: ({ children, ...props }) => <h1 className="text-2xl font-bold my-2" {...props}>{renderWithCaret(children)}</h1>,
                  h2: ({ children, ...props }) => <h2 className="text-xl font-bold my-2" {...props}>{renderWithCaret(children)}</h2>,
                  h3: ({ children, ...props }) => <h3 className="text-lg font-bold my-2" {...props}>{renderWithCaret(children)}</h3>,
                }}
              >
                {sanitizeChatContent(!isOwn && message.isChunk && !message.isComplete ? message.content + ' ||cursor||' : message.content)}
              </ReactMarkdown>
            ) : null}
          </div>

          {/* Citations (EMMA-HARDEN-002: Inline citation badges) */}
          {message.metadata?.citations && message.metadata.citations.length > 0 && (
            <div className="mt-3">
              <CitationBadge 
                sources={message.metadata.citations} 
                qualityScore={message.metadata.qualityScore || 0.8}
              />
            </div>
          )}

          {/* Metadata content */}
          {renderRecommendations()}
          {renderStrategicInsights()}
          {renderWorkflowSuggestions()}

          {/* Standalone Artifact Panel Button */}
          {(() => {
            const isAssetMessage = !isOwn && (
              Boolean(message.metadata?.artifact) ||
              Boolean(message.metadata?.ui_action) ||
              Boolean(message.metadata?.isStatelessAsset) ||
              Boolean(message.metadata?.statelessAsset) ||
              Boolean(message.metadata?.assetType) ||
              (typeof message.content === 'string' && (
                message.content.includes('Asset Generated') ||
                message.content.includes('Autonomous Asset Generation') ||
                message.content.includes('Document Generated') ||
                message.content.includes('Open Template Preview') ||
                message.content.includes('Template Content Updated') ||
                message.content.includes('Styling Updated') ||
                message.content.includes('Poster') ||
                message.content.includes('Battlecard')
              ))
            );
            return isAssetMessage ? (
              <div className="mt-3 pt-2 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => {
                    const artifactPayload = extractArtifactPayload(message);
                    window.dispatchEvent(new CustomEvent('emma-open-artifact-panel', {
                      detail: artifactPayload
                    }));
                  }}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium text-sm flex items-center gap-2 shadow-md transition-all cursor-pointer hover:bg-emerald-700 active:scale-95"
                >
                  <span>📄</span> Open Artifact Panel
                </button>
                <button
                  onClick={() => {
                    const artifactPayload = extractArtifactPayload(message);
                    const previewUrl = artifactPayload?.previewUrl || (artifactPayload?.slug ? `/api/templates/preview/${artifactPayload.slug}` : null);
                    if (previewUrl) {
                      const fullUrl = previewUrl.startsWith('/api/') ? buildApiUrl(previewUrl) : previewUrl;
                      window.open(fullUrl, '_blank');
                      return;
                    }
                    if (artifactPayload?.content && artifactPayload.content.includes('<')) {
                      const blob = new Blob([artifactPayload.content], { type: 'text/html;charset=utf-8' });
                      const blobUrl = URL.createObjectURL(blob);
                      window.open(blobUrl, '_blank');
                    }
                  }}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg font-medium text-sm flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                  title="Open artifact in full browser tab"
                >
                  <span>🌐</span> Open in New Tab
                </button>
              </div>
            ) : null;
          })()}

          {/* Stateless Asset Rendering (UniversalPreview) */}
          {message.metadata?.isStatelessAsset && message.metadata?.statelessAsset && (() => {
            const assetPayload = message.metadata.statelessAsset;
            const assetObj = assetPayload.asset || {};
            const asset_type = message.metadata.assetType || 'battlecard';
            const bodyContent = assetObj.deep_dive || assetObj.all || Object.values(assetObj)[0] || '';
            
            return (
              <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4 text-gray-900 dark:text-gray-100">
                <UniversalPreview
                  content_type={asset_type}
                  content_body={bodyContent}
                  subject={asset_type.toUpperCase()}
                  piece={{
                    layout_type: 'genui_html',
                    dynamic_layout: true
                  }}
                />
                
                <div className="mt-4 flex flex-col gap-2">
                  <button
                    onClick={handleSaveToCampaign}
                    disabled={isSaving || saveStatus === 'success'}
                    className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                      saveStatus === 'success'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 cursor-not-allowed'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50'
                    }`}
                  >
                    {isSaving && (
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    {saveStatus === 'success' ? '✓ Saved to Campaign' : 'Save to Campaign'}
                  </button>
                  
                  {saveStatus === 'success' && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-semibold">
                      Successfully saved as campaign: <strong>{savedCampaignName}</strong>!
                    </p>
                  )}
                  
                  {saveStatus === 'error' && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-semibold">
                      ⚠️ {errorMessage}
                    </p>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Timestamp */}
          {showTimestamp && (
            <div className={`mt-2 text-xs ${
              isOwn 
                ? 'text-primary-100' 
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              {formatTimestamp(message.timestamp)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
